import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { APP_VARIANT } from '../config/appVariant';
import { splitVisualDirectives } from '../components/messageDirectives';
import { setAuthToken, clearAuthToken, AUTH_EXPIRED_EVENT } from '../utils/authToken';
import { clearUserBrowserData } from '../utils/userBrowserData';
import { fetchWithTimeout, DOCUMENT_TIMEOUT_MS } from '../utils/webhook';

// ===== WEBHOOK BASE URL =====
// Read from environment at build time so dev/staging/prod each point
// at the correct n8n instance. Configure REACT_APP_N8N_BASE_URL in:
//   - .env.local      (local development)
//   - Netlify deploy contexts (per-branch env vars)
// Falls back to localhost when not set.
const N8N_BASE_URL =
  process.env.REACT_APP_N8N_BASE_URL || 'http://localhost:5678';

// ===== WEBHOOK CONFIGURATION =====
export const WEBHOOKS = {
  // Authentication
  LOGIN: `${N8N_BASE_URL}/webhook/login`,
  SIGNUP: `${N8N_BASE_URL}/webhook/signup`,
  // Answered by the sign-in gate (not n8n): a guest id plus its token.
  AUTH_GUEST: `${N8N_BASE_URL}/webhook/auth-guest`,

  // Chat & History
  TEACHING_ASSISTANT: `${N8N_BASE_URL}/webhook/teaching-assistant`,
  LOAD_CHAT_MESSAGES: `${N8N_BASE_URL}/webhook/load_chat_messages`,
  DELETE_CHAT: `${N8N_BASE_URL}/webhook/delete_chat`,
  HEADER: `${N8N_BASE_URL}/webhook/header`,

  // Homework Generator
  DOCUMENT: `${N8N_BASE_URL}/webhook/document`,
  COWRITER: `${N8N_BASE_URL}/webhook/cowriter`,
  // Tools the document agent asks the frontend to call (see utils/agentTools).
  DOCUMENT_GENERATE: `${N8N_BASE_URL}/webhook/document-generate`,
  // The general build's `generation` tool: workplace documents, not homework questions.
  GENERAL_DOCUMENT: `${N8N_BASE_URL}/webhook/general_document`,
  COWRITER_SPLICE: `${N8N_BASE_URL}/webhook/cowriter-splice`,
  AUTOSAVE: `${N8N_BASE_URL}/webhook/autosave`,
  LOAD_DOCUMENT_HISTORY: `${N8N_BASE_URL}/webhook/load_document_history`,
  LOAD_DOCUMENT: `${N8N_BASE_URL}/webhook/load_document`,
  DELETE_DOCUMENT: `${N8N_BASE_URL}/webhook/delete_document`,

  //Homework Corrector
  CORRECTION: `${N8N_BASE_URL}/webhook/correction`,
  AUTOSAVE_CORRECTION: `${N8N_BASE_URL}/webhook/autosave_correction`,
  LOAD_CORRECTION_HISTORY: `${N8N_BASE_URL}/webhook/load_correction_history`,
  LOAD_CORRECTION: `${N8N_BASE_URL}/webhook/load_correction`,
  // Dashboard table: graded files across the user's corrections, newest first (paged).
  CORRECTION_RECENT_FILES: `${N8N_BASE_URL}/webhook/correction_recent_files`,
  DELETE_CORRECTION: `${N8N_BASE_URL}/webhook/delete_correction`,

  // Knowledge Base
  UPLOAD_FILE: `${N8N_BASE_URL}/webhook/upload-knowledge-file`,
  DELETE_FILE: `${N8N_BASE_URL}/webhook/delete-knowledge-file`,
  LOAD_KB_FILES: `${N8N_BASE_URL}/webhook/load-knowledge-files`,

  //Deep analysis
  ANALYTICS: `${N8N_BASE_URL}/webhook/teacher-analytics`,

  // Data Analysis Platform
  DATA_ANALYSIS: `${N8N_BASE_URL}/webhook/data-analysis`,

  // Content Generator
  WORKSHEET_GENERATION: `${N8N_BASE_URL}/webhook/worksheet-generation`,
  EDIT_WORKSHEET: `${N8N_BASE_URL}/webhook/edit-worksheet`,
  EXPORT_WORKSHEET: `${N8N_BASE_URL}/webhook/export-worksheet`,
  GENERATE_COURSE_PLAN: `${N8N_BASE_URL}/webhook/generate-course-plan`,
  EXPORT_COURSE_PLAN: `${N8N_BASE_URL}/webhook/export-course-plan`,
  SUGGESTED_VIDEOS: `${N8N_BASE_URL}/webhook/suggested-videos`,
  GENERATE_ACTIVITIES: `${N8N_BASE_URL}/webhook/generate-activities`,
  EDIT_ACTIVITIES: `${N8N_BASE_URL}/webhook/edit-activities`,
  EXPORT_ACTIVITIES: `${N8N_BASE_URL}/webhook/export-activities`,
  PRESENTATION_PLAN: `${N8N_BASE_URL}/webhook/presentation-plan`,
  PRESENTATION_EDIT: `${N8N_BASE_URL}/webhook/presentation-edit`,
  PRESENTATION_BUILD: `${N8N_BASE_URL}/webhook/presentation-build`,
  PRESENTATION_REGENERATE_IMAGE: `${N8N_BASE_URL}/webhook/presentation-regenerate-image`,

  // Voice Assistant
  VOICE_ASSISTANT: `${N8N_BASE_URL}/webhook/voice-assistant`,

  // Image generation (OpenRouter image model via n8n)
  GENERATE_IMAGE: `${N8N_BASE_URL}/webhook/generate-image`,

  // The sidebar's support-and-opinion page (features/feedback). Separate from
  // MESSAGE_FEEDBACK below: that rates one answer, this rates the assistant.
  PLATFORM_FEEDBACK: `${N8N_BASE_URL}/webhook/platform-feedback`,

  // Per-message thumbs up/down on assistant answers. The workflow upserts on
  // `feedbackId`, because one rating is sent up to twice: once the moment the
  // thumb is clicked, and again if the teacher goes on to pick reasons or
  // write details. See MessageFeedback.jsx for the payload.
  MESSAGE_FEEDBACK: `${N8N_BASE_URL}/webhook/message-feedback`,

  // Data sources (external databases queried through text-to-SQL).
  // These proxy to the crewai "School Analyzer" service; n8n holds the
  // service bearer token so it never reaches the browser bundle. The
  // browser posts credentials exactly once (DATASOURCE_CREATE) and
  // thereafter refers to the connection by its server-issued id.
  DATASOURCES_LIST: `${N8N_BASE_URL}/webhook/datasources-list`,
  DATASOURCE_CREATE: `${N8N_BASE_URL}/webhook/datasource-create`,
  DATASOURCE_TEST: `${N8N_BASE_URL}/webhook/datasource-test`,
  DATASOURCE_DELETE: `${N8N_BASE_URL}/webhook/datasource-delete`,

  // Tasks & follow-ups
  TASKS_LIST: `${N8N_BASE_URL}/webhook/tasks-list`,
  TASK_CREATE: `${N8N_BASE_URL}/webhook/task-create`,
  TASK_UPDATE: `${N8N_BASE_URL}/webhook/task-update`,
  TASK_DELETE: `${N8N_BASE_URL}/webhook/task-delete`,

  // Scholarships / الابتعاث
  SCHOLARSHIP_APPLICANTS: `${N8N_BASE_URL}/webhook/scholarship-applicants`,
  SCHOLARSHIP_CHECK:      `${N8N_BASE_URL}/webhook/scholarship-check`,
  SCHOLARSHIP_EXPLAIN:    `${N8N_BASE_URL}/webhook/scholarship-explain`,
  SCHOLARSHIP_PIPELINE:   `${N8N_BASE_URL}/webhook/scholarship-pipeline`,
  SCHOLARSHIP_LETTER:     `${N8N_BASE_URL}/webhook/scholarship-letter`,
  USERS_DIRECTORY: `${N8N_BASE_URL}/webhook/users-directory`,

  // Frontend error reports (see utils/errorLog.js)
  LOG_ERROR: `${N8N_BASE_URL}/webhook/log-error`
};

// ===== LOCAL STORAGE KEYS =====
const STORAGE_KEYS = {
  THEME: 'theme',
  CURRENT_USER: 'currentUser',
  CHAT_HISTORY: 'chatHistory',
  CURRENT_CHAT: 'currentChat',
  KNOWLEDGE_FILES: 'knowledgeFiles',
  SELECTED_FILES: 'selectedFiles',
  SELECTED_BOOK: 'selectedBook',
  SELECTED_BOOK_LABEL: 'selectedBookLabel',
  // Only the server-issued connection id and its display label are stored.
  // Database credentials are NEVER persisted client-side — they are posted
  // once to the server-side store and never returned to the browser.
  SELECTED_DATASOURCE: 'selectedDataSource',
  SELECTED_DATASOURCE_LABEL: 'selectedDataSourceLabel',
  DOCUMENT_HISTORY: 'documentHistory',
  CURRENT_DOCUMENT: 'currentDocument',
  CORRECTION_HISTORY: 'correctionHistory',
};

// Every per-user key is the teacher's own work and is removed at logout (shared school
// computers). The theme and the signed-in user record are not per-user work.
const USER_DATA_KEYS = Object.values(STORAGE_KEYS)
  .filter((key) => key !== STORAGE_KEYS.THEME && key !== STORAGE_KEYS.CURRENT_USER);

// Helper to get user-specific storage key
const getUserStorageKey = (baseKey, userId) => `${baseKey}_${userId}`;

const GUEST_USER = {
  id: 'guest_123',
  username: 'Guest User',
  email: 'guest@example.com',
  isGuest: true
};

// Initial state for a per-user key: the saved logged-in user's value, else fallback.
const readUserStorage = (baseKey, fallback, json = true) => {
  try {
    const user = JSON.parse(localStorage.getItem(STORAGE_KEYS.CURRENT_USER));
    if (!user || user.isGuest) return fallback;
    const raw = localStorage.getItem(getUserStorageKey(baseKey, user.id));
    if (!raw) return fallback;
    return json ? JSON.parse(raw) : raw;
  } catch (e) {
    return fallback;
  }
};

// Mirror a per-user value to localStorage. Strings are removed when empty.
const usePersistUser = (baseKey, value, currentUser, json = true) => {
  useEffect(() => {
    if (!currentUser || currentUser.isGuest) return;
    const key = getUserStorageKey(baseKey, currentUser.id);
    if (json) localStorage.setItem(key, JSON.stringify(value));
    else if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  }, [baseKey, value, currentUser, json]);
};

/**
 * Normalize chat object to ensure it has sessionId property
 */
const normalizeChat = (chat) => {
  if (!chat) return null;
  const sessionId = chat.sessionId || chat.id;
  if (!sessionId) {
    console.warn('⚠️ Chat missing sessionId:', chat);
    return null;
  }
  return {
    ...chat,
    sessionId: sessionId,
    messages: chat.messages || [],
    messagesLoaded: chat.messagesLoaded || false,
    // No invented "now": an unknown time shows as blank in the sidebar.
    timestamp: chat.timestamp || null
  };
};

/**
 * Normalize chat history array
 */
const normalizeChatHistory = (history) => {
  if (!Array.isArray(history)) return [];
  return history.map(normalizeChat).filter(chat => chat !== null);
};

// Image blocks can carry multi-MB base64 data URLs. Keep them in memory for the
// live session, but strip them before writing to localStorage — a couple of
// generated images would otherwise blow the ~5MB quota and crash the app.
// Attached files are treated the same way: only a small photo keeps its
// picture (for the thumbnail); other files keep their name and size.
// A failed reply bubble (with its Retry button) is not kept either.
const MAX_CACHED_THUMB = 200 * 1024;
const stripHeavyBlocks = (messages) =>
  (messages || []).filter((m) => !m?.failed).map((m) => {
    if (!m) return m;
    let out = m;
    if (Array.isArray(m.attachments) && m.attachments.some((a) => a?.dataUrl)) {
      const attachments = m.attachments.map((a) =>
        a && a.dataUrl && !(a.fileType === 'image' && a.dataUrl.length <= MAX_CACHED_THUMB)
          ? { ...a, dataUrl: '' }
          : a
      );
      out = { ...out, attachments };
    }
    if (!Array.isArray(m.blocks) || m.blocks.length === 0) return out;
    const blocks = m.blocks.map((b) =>
      b && typeof b.url === 'string' && b.url.startsWith('data:')
        ? { ...b, url: '', omitted: true }
        : b
    );
    return { ...out, blocks };
  });

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Theme state
  // Follows the OS appearance until the person picks one with the toggle;
  // only an explicit choice is saved.
  const systemTheme = () =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.THEME) || systemTheme();
  });

  // User state
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    return savedUser ? JSON.parse(savedUser) : GUEST_USER;
  });

  // Authentication state
  // GUEST_USER itself is the signed-out placeholder; a guest who chose
  // "continue as guest" has their own id and stays signed in across reloads.
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !currentUser.isGuest || currentUser.id !== GUEST_USER.id
  );

  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [directory, setDirectory] = useState([]);

  // Chat state
  const [chatHistory, setChatHistory] = useState(() =>
    normalizeChatHistory(readUserStorage(STORAGE_KEYS.CHAT_HISTORY, []))
  );

  // Always start on a fresh, empty chat when the app loads. Previous chats are
  // still restored into chatHistory (the sidebar) above and can be reopened
  // from there — we just no longer auto-reopen the last one on page load.
  const [currentChat, setCurrentChat] = useState(null);

  // ===== DOCUMENT HISTORY STATE =====
  const [documentHistory, setDocumentHistory] = useState(() =>
    readUserStorage(STORAGE_KEYS.DOCUMENT_HISTORY, [])
  );

  const [currentDocument, setCurrentDocument] = useState(null);

  // Every open fetches the document again, so it always shows the latest copy.
  // documentLoads counts opens: a reply for an older click is dropped.
  const documentLoads = useRef(0);
  // Autosaves still on their way, by sessionId. Opening that document waits for
  // them, or the copy fetched could be older than what the teacher typed.
  const documentSaves = useRef({});

  // Correction history
  const [correctionHistory, setCorrectionHistory] = useState(() =>
    // Older caches stored full results per entry; keep the summary only.
    readUserStorage(STORAGE_KEYS.CORRECTION_HISTORY, []).map(({ results, ...summary }) => summary)
  );

  const [currentCorrection, setCurrentCorrection] = useState(null);

  // Knowledge Base state
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState(() =>
    readUserStorage(STORAGE_KEYS.KNOWLEDGE_FILES, [])
  );
  const [selectedFiles, setSelectedFiles] = useState(() =>
    readUserStorage(STORAGE_KEYS.SELECTED_FILES, [])
  );
  const [selectedBook, setSelectedBook] = useState(() =>
    readUserStorage(STORAGE_KEYS.SELECTED_BOOK, null, false)
  );
  const [selectedBookLabel, setSelectedBookLabel] = useState(() =>
    readUserStorage(STORAGE_KEYS.SELECTED_BOOK_LABEL, null, false)
  );

  // ===== DATA SOURCES (external databases) =====
  // `dataSources` is the catalogue fetched from the server; `selectedDataSource`
  // is the connection the chat should currently query. Mirrors the selectedBook
  // pattern above, except the persisted value is only an opaque connection id.
  const [dataSources, setDataSources] = useState([]);
  const [dataSourcesLoading, setDataSourcesLoading] = useState(false);
  const [dataSourcesError, setDataSourcesError] = useState(null);

  const [selectedDataSource, setSelectedDataSource] = useState(() =>
    readUserStorage(STORAGE_KEYS.SELECTED_DATASOURCE, null, false)
  );
  const [selectedDataSourceLabel, setSelectedDataSourceLabel] = useState(() =>
    readUserStorage(STORAGE_KEYS.SELECTED_DATASOURCE_LABEL, null, false)
  );

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // ===== PERSISTENCE EFFECTS =====

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => {
      if (!localStorage.getItem(STORAGE_KEYS.THEME)) setTheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(currentUser));
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && !currentUser.isGuest && chatHistory.length > 0) {
      const storageKey = getUserStorageKey(STORAGE_KEYS.CHAT_HISTORY, currentUser.id);
      const normalizedHistory = normalizeChatHistory(chatHistory)
        .map((c) => ({ ...c, messages: stripHeavyBlocks(c.messages) }));
      try {
        localStorage.setItem(storageKey, JSON.stringify(normalizedHistory));
      } catch (e) {
        console.warn('Chat history not persisted (storage full?):', e?.name || e);
      }
    }
  }, [chatHistory, currentUser]);

  // Persist document history
  useEffect(() => {
    if (currentUser && !currentUser.isGuest && documentHistory.length > 0) {
      const storageKey = getUserStorageKey(STORAGE_KEYS.DOCUMENT_HISTORY, currentUser.id);
      localStorage.setItem(storageKey, JSON.stringify(documentHistory));
    }
  }, [documentHistory, currentUser]);

  usePersistUser(STORAGE_KEYS.CORRECTION_HISTORY, correctionHistory, currentUser);
  usePersistUser(STORAGE_KEYS.KNOWLEDGE_FILES, knowledgeBaseFiles, currentUser);
  usePersistUser(STORAGE_KEYS.SELECTED_FILES, selectedFiles, currentUser);
  usePersistUser(STORAGE_KEYS.SELECTED_BOOK, selectedBook, currentUser, false);
  usePersistUser(STORAGE_KEYS.SELECTED_BOOK_LABEL, selectedBookLabel, currentUser, false);
  usePersistUser(STORAGE_KEYS.SELECTED_DATASOURCE, selectedDataSource, currentUser, false);
  usePersistUser(STORAGE_KEYS.SELECTED_DATASOURCE_LABEL, selectedDataSourceLabel, currentUser, false);

  // ===== AUTHENTICATION FUNCTIONS =====

  const login = async (username, password) => {
    try {
      const response = await fetch(WEBHOOKS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      let data = await response.json().catch(() => ({}));
      if (Array.isArray(data)) data = data[0] || {};

      if (response.ok && data.success === true && data.user && data.user.id) {
        const userData = {
          id: data.user.id,
          username: data.user.username,
          email: data.user.email,
          avatar: data.user.avatar || null,
          isGuest: data.user.isGuest || false
        };

        setAuthToken(data.authToken || '');
        setCurrentUser({ ...userData, token: data.authToken || data.token || 'no-token-provided' });
        setIsAuthenticated(true);

        const cachedHistory = localStorage.getItem(
          getUserStorageKey(STORAGE_KEYS.CHAT_HISTORY, userData.id)
        );

        if (data.chatHeaders && Array.isArray(data.chatHeaders)) {
          const newChatHistory = data.chatHeaders.map(header => ({
            sessionId: header.session_id,
            title: header.chat_header,
            userId: data.user.id,
            email: data.user.email,
            fullName: data.user.username,
            timestamp: header.timestamp || header.updated_at || header.created_at || null,
            lastMessage: '',
            messageCount: 0,
            messages: [],
            messagesLoaded: false
          }));

          const normalizedHistory = normalizeChatHistory(newChatHistory);
          let mergedHistory = normalizedHistory;

          if (cachedHistory) {
            try {
              const cached = normalizeChatHistory(JSON.parse(cachedHistory));
              const serverSessionIds = new Set(normalizedHistory.map(c => c.sessionId));
              const uniqueCachedChats = cached.filter(c => !serverSessionIds.has(c.sessionId));
              if (uniqueCachedChats.length > 0) {
                mergedHistory = [...normalizedHistory, ...uniqueCachedChats];
              }
            } catch (e) {
              console.error('[Cache] Could not merge unsynced cached chats; using the server list only:', e);
            }
          }

          setChatHistory(mergedHistory);
        } else if (cachedHistory) {
          try {
            const cached = normalizeChatHistory(JSON.parse(cachedHistory));
            setChatHistory(cached);
          } catch (e) { setChatHistory([]); }
        } else {
          setChatHistory([]);
        }

        // Also load document history, correction history and knowledge base files on login
        // Force the server copy: older caches hold login-time timestamps.
        loadDocumentHistory(userData.id, true);
        loadCorrectionHistory(userData.id);
        loadKnowledgeBaseFiles(userData.id);

        showNotification('Login successful!', 'success');
        return { success: true, user: userData };
      } else {
        throw new Error(data.error || data.message || '');
      }
    } catch (error) {
      console.error('Login error:', error);
      // The sign-in page shows the reason beside the form. fetch() throws
      // TypeError when the server can't be reached at all.
      return { success: false, error: error.message, network: error instanceof TypeError };
    }
  };

  const signup = async (username, email, password) => {
    try {
      const response = await fetch(WEBHOOKS.SIGNUP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password })
      });

      let data = await response.json().catch(() => ({}));
      if (Array.isArray(data)) data = data[0] || {};

      if (response.ok && data.success && data.user) {
        const userData = {
          id: data.user.id,
          username: data.user.username,
          email: data.user.email,
          avatar: data.user.avatar || null,
          isGuest: false
        };

        setAuthToken(data.authToken || '');
        setCurrentUser(userData);
        setIsAuthenticated(true);
        setChatHistory([]);
        setDocumentHistory([]);

        showNotification('Account created successfully!', 'success');
        return { success: true, user: userData };
      } else {
        throw new Error(data.error || data.message || '');
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message, network: error instanceof TypeError };
    }
  };

  const logout = () => {
    const userId = currentUser?.id;
    // Chats, documents, marks history, selected book, upload jobs and older shared
    // autosave copies: nothing of this teacher's work stays in the browser.
    clearUserBrowserData(userId, USER_DATA_KEYS);

    clearAuthToken();
    setCurrentUser(GUEST_USER);
    setIsAuthenticated(false);
    setChatHistory([]);
    setCurrentChat(null);
    setDocumentHistory([]);
    setCurrentDocument(null);
    documentLoads.current += 1;
    setKnowledgeBaseFiles([]);
    setSelectedFiles([]);
    setSelectedBook(null);
    setSelectedBookLabel(null);
    setDataSources([]);
    setSelectedDataSource(null);
    setSelectedDataSourceLabel(null);
    setCorrectionHistory([]);
    setCurrentCorrection(null);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    showNotification('Logged out successfully', 'success');
  };

  const setGuestUser = async () => {
    let id = `guest_${Date.now()}`;
    clearAuthToken();
    try {
      const res = await fetch(WEBHOOKS.AUTH_GUEST, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data?.guestId && data?.authToken) {
        id = data.guestId;
        setAuthToken(data.authToken);
      }
    } catch {
      // No sign-in gate in front of this backend: keep the local guest id.
    }
    setCurrentUser({ ...GUEST_USER, id });
    setIsAuthenticated(true);
    setChatHistory([]);
    setCurrentChat(null);
    setDocumentHistory([]);
    setCurrentDocument(null);
    documentLoads.current += 1;
  };

  // The sign-in gate answers 401 when the token is missing or has expired: sign out cleanly.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  useEffect(() => {
    const onExpired = () => {
      logoutRef.current();
      showNotification('Your session has ended. Please sign in again.', 'error');
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
    // showNotification only sets state; the listener is registered once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== CHAT HISTORY FUNCTIONS =====

  const loadChatMessages = async (sessionId, userId) => {
    try {
      const response = await fetch(WEBHOOKS.LOAD_CHAT_MESSAGES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, userId })
      });

      if (!response.ok) throw new Error('Failed to load messages');

      let data = await response.json();

      if (Array.isArray(data)) {
        const messages = data.map((item, index) => {
          const msg = item.message || item;
          const role = msg.type === 'human' ? 'user' : 'assistant';
          // Assistant replies are stored with their inline directives intact, so
          // they must be parsed here exactly as the live reply was. Without this
          // a reopened conversation printed raw [[CHART: {...}]] JSON as text and
          // lost the chart or map entirely.
          const parsed = role === 'assistant'
            ? splitVisualDirectives(msg.content || '')
            : { text: msg.content || '', blocks: [] };
          return {
            id: item.id?.toString() || `msg_${index}`,
            role,
            content: parsed.text,
            ...(parsed.blocks.length ? { blocks: parsed.blocks } : {}),
            timestamp: item.timestamp || new Date().toISOString()
          };
        });
        return messages;
      }

      return [];
    } catch (error) {
      console.error('Load messages error:', error);
      showNotification('Failed to load chat messages', 'error');
      return [];
    }
  };

  const generateChatHeader = async (sessionId, firstMessage, documentTitles = []) => {
    try {
      const response = await fetch(WEBHOOKS.HEADER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          message: firstMessage,
          documentTitles,
          userId: currentUser?.id
        })
      });

      if (!response.ok) throw new Error('Failed to generate header');

      let data = await response.json();
      if (Array.isArray(data) && data.length > 0) data = data[0];

      if (data.title || data.header) {
        return data.title || data.header;
      }
      return null;
    } catch (error) {
      console.error('Generate header error:', error);
      return null;
    }
  };

  // Uses the latest currentChat, not the one this function was made with. A chat
  // started on the empty screen is created in the same send that asks for its
  // title, so the old value is still null; checking it left the open chat
  // without its title, and the next reply asked for (and saved) a second one.
  const updateChatTitle = (sessionId, newTitle) => {
    setChatHistory(prev =>
      prev.map(chat => (chat.sessionId || chat.id) === sessionId ? { ...chat, title: newTitle } : chat)
    );
    setCurrentChat(prev =>
      prev && (prev.sessionId || prev.id) === sessionId ? { ...prev, title: newTitle } : prev
    );
  };

  // ===== DOCUMENT HISTORY FUNCTIONS =====

  /**
   * Load document history from server
   * Backend returns: [{ documentTitle: "...", sessionId: "..." }, ...]
   */
  const loadDocumentHistory = async (userId, forceRefresh = false) => {
    try {
      if (!forceRefresh) {
        const cached = localStorage.getItem(
          getUserStorageKey(STORAGE_KEYS.DOCUMENT_HISTORY, userId || currentUser?.id)
        );
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed.length > 0) {
              setDocumentHistory(parsed);
              return;
            }
          } catch (e) {
            console.error('[Cache] Document history cache unreadable; loading from the server:', e);
          }
        }
      }

      const response = await fetch(WEBHOOKS.LOAD_DOCUMENT_HISTORY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userId || currentUser?.id })
      });

      if (!response.ok) throw new Error('Failed to load document history');

      let data = await response.json();

      // Handle nested array: [[{...}]] -> [{...}]
      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
        data = data[0];
      }

      // Ensure it's an array
      if (!Array.isArray(data)) data = [data];

      // Parse: [{ documentTitle, sessionId }]
      const docs = data
        .filter(doc => doc && (doc.sessionId || doc.session_id || doc.id))
        .map(doc => ({
          sessionId: doc.sessionId || doc.session_id || doc.id,
          title: doc.documentTitle || doc.title || 'Untitled',
          timestamp: doc.timestamp || doc.lastModified || doc.updated_at || doc.created_at || null,
        }));

      // Deduplicate by sessionId
      const unique = docs.reduce((acc, doc) => {
        if (!acc.find(d => d.sessionId === doc.sessionId)) acc.push(doc);
        return acc;
      }, []);

      setDocumentHistory(unique);
    } catch (error) {
      console.error('Load document history error:', error);
      const cached = localStorage.getItem(
        getUserStorageKey(STORAGE_KEYS.DOCUMENT_HISTORY, userId || currentUser?.id)
      );
      if (cached) {
        try { setDocumentHistory(JSON.parse(cached)); } catch (e) { setDocumentHistory([]); }
      }
    }
  };

  /**
   * Load a full document by sessionId, always from the server.
   * There used to be a session cache here that autosave never updated, so a
   * document opened a second time showed the old copy, and the next keystroke
   * saved that old copy over the newer one.
   * Backend returns autosave shape:
   * { documentTitle, documentContent, metadata: { subject, educationLevel, date }, chatMessages? }
   */
  const loadDocument = async (sessionId) => {
    const load = ++documentLoads.current;
    try {
      // A failed save is the editor's to report; the open goes ahead.
      await documentSaves.current[sessionId]?.catch(() => {});
      if (load !== documentLoads.current) return null;

      const response = await fetchWithTimeout(WEBHOOKS.LOAD_DOCUMENT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          userId: currentUser?.id
        })
      }, DOCUMENT_TIMEOUT_MS);

      if (!response.ok) throw new Error('Failed to load document');

      let data = await response.json();
      // The teacher clicked another document (or started a new one) meanwhile.
      if (load !== documentLoads.current) return null;

      // Handle nested array: [[{...}]] or [{...}]
      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) data = data[0];
      if (Array.isArray(data) && data.length > 0) data = data[0];

      // Parse autosave shape: { documentTitle, documentContent, metadata: { subject, educationLevel, date } }
      const metadata = data.metadata || {};
      const doc = {
        sessionId,
        title: data.documentTitle || data.title || 'Untitled',
        content: data.documentContent || data.content || '',
        subject: metadata.subject || data.subject || '',
        educationLevel: metadata.educationLevel || data.educationLevel || '',
        date: metadata.date || data.date || '',
        // General build; '' for documents saved before these existed.
        documentType: metadata.documentType || '',
        confidentiality: metadata.confidentiality || '',
        chatMessages: data.chatMessages || data.messages || [],
        loaded: true,
      };

      setCurrentDocument(doc);
      return doc;
    } catch (error) {
      console.error('Load document error:', error);
      if (load === documentLoads.current) showNotification('Failed to load document', 'error');
      return null;
    }
  };

  /**
   * The editor reports each autosave here while it is on its way (see loadDocument).
   */
  const trackDocumentSave = (sessionId, save) => {
    const pending = Promise.allSettled([documentSaves.current[sessionId], save]);
    documentSaves.current[sessionId] = pending;
    pending.then(() => {
      if (documentSaves.current[sessionId] === pending) delete documentSaves.current[sessionId];
    });
  };

  /**
   * The editor leaves its latest copy here when it closes, so coming back to
   * the page (without opening the document again) shows that copy.
   */
  const keepDocumentCopy = (sessionId, fields) => {
    setCurrentDocument(prev => (prev?.sessionId === sessionId
      ? { ...prev, ...fields, loaded: true, isNew: false }
      : prev));
  };

  /**
   * Create a new document (resets currentDocument)
   */
  const createNewDocument = () => {
    // A document still loading from an earlier click must not replace this one.
    documentLoads.current += 1;
    const newSessionId = crypto.randomUUID();
    const newDoc = {
      sessionId: newSessionId,
      title: '',
      content: '',
      subject: '',
      educationLevel: '',
      date: new Date().toISOString().split('T')[0],
      chatMessages: [],
      loaded: true,
      isNew: true,
    };
    setCurrentDocument(newDoc);
    return newDoc;
  };

  /**
   * Add or update a document in history (called after autosave).
   */
  const upsertDocumentInHistory = (sessionId, title, timestamp) => {
    setDocumentHistory(prev => {
      const exists = prev.find(d => d.sessionId === sessionId);
      if (exists) {
        return prev.map(d => d.sessionId === sessionId
          ? { ...d, title: title || d.title, timestamp: timestamp || new Date().toISOString() }
          : d
        );
      }
      return [{ sessionId, title: title || 'Untitled', timestamp: timestamp || new Date().toISOString() }, ...prev];
    });
  };

  /**
   * Ask a delete webhook to remove one session. Every delete webhook takes
   * { userId, sessionId } and answers [{ success: true }]; anything else counts
   * as a failure, so callers only drop the item locally once the server has.
   */
  const deleteOnServer = async (url, sessionId) => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser?.id, sessionId })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if ((Array.isArray(data) ? data[0] : data)?.success !== true) throw new Error('Webhook did not confirm the delete');
      return true;
    } catch (error) {
      console.error(`[Delete] ${url} failed:`, error);
      return false;
    }
  };

  /**
   * Delete a document on the server, then from history. Returns whether it worked.
   */
  const deleteDocument = async (sessionId) => {
    if (!(await deleteOnServer(WEBHOOKS.DELETE_DOCUMENT, sessionId))) return false;
    setDocumentHistory(prev => prev.filter(d => d.sessionId !== sessionId));
    if (currentDocument?.sessionId === sessionId) {
      setCurrentDocument(null);
    }
    return true;
  };

  // ===== CORRECTION HISTORY FUNCTIONS =====

  /**
   * Load correction history list from server on login.
   * Falls back to localStorage cache if server fails.
   */
  // Newest first, whatever order the server/cache returns (unparseable dates last).
  const newestFirst = (entries) =>
    [...entries].sort((a, b) => (Date.parse(b.timestamp) || 0) - (Date.parse(a.timestamp) || 0));

  const loadCorrectionHistory = async (userId) => {
    try {
      // Show cached data immediately while fetching
      const cached = localStorage.getItem(
        getUserStorageKey(STORAGE_KEYS.CORRECTION_HISTORY, userId || currentUser?.id)
      );
      if (cached) {
        try {
          // Older caches stored full results per entry; keep the summary only.
          const parsed = JSON.parse(cached).map(({ results, ...summary }) => summary);
          if (parsed.length > 0) setCorrectionHistory(newestFirst(parsed));
        } catch (e) {
          console.error('[Cache] Correction history cache unreadable; loading from the server:', e);
        }
      }

      const response = await fetch(WEBHOOKS.LOAD_CORRECTION_HISTORY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userId || currentUser?.id })
      });
      if (!response.ok) throw new Error('Failed to load correction history');

      let data = await response.json();

      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) data = data[0];
      if (!Array.isArray(data)) data = data.corrections ? data.corrections : (data.sessionId ? [data] : []);

      const entries = data
        .filter(d => d && (d.sessionId || d.session_id))
        .map(d => ({
          sessionId: d.sessionId || d.session_id,
          title: d.title || 'Correction',
          timestamp: d.timestamp || d.updated_at || d.created_at || null,
          stats: d.stats || null,
          // results are fetched on demand via loadCorrectionSession
        }));

      setCorrectionHistory(newestFirst(entries));
    } catch (error) {
      console.error('[Corrector] Load history error:', error);
    }
  };

  /**
   * Add/refresh a correction in the local history list so the sidebar and
   * dashboard update without a reload. Local state only — the server copy is
   * written by the corrector's autosave (same sessionId overwrites).
   * Holds the summary the server returns (no results) to keep memory and
   * localStorage small.
   */
  const upsertCorrectionHistory = ({ sessionId, title, stats, timestamp }) => {
    const entry = { sessionId, title: title || 'Correction', stats, timestamp };
    setCorrectionHistory(prev => [entry, ...prev.filter(c => c.sessionId !== sessionId)]);
    setCurrentCorrection(entry);
  };

  /**
   * Load full results for a correction session from the server.
   * Results live only on currentCorrection, never in the history list.
   */
  const loadCorrectionSession = async (sessionId) => {
    const cached = correctionHistory.find(c => c.sessionId === sessionId);

    try {
      const response = await fetch(WEBHOOKS.LOAD_CORRECTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser?.id, sessionId })
      });
      if (!response.ok) throw new Error('Failed to load correction');

      let data = await response.json();
      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) data = data[0];
      if (Array.isArray(data) && data.length > 0) data = data[0];

      const entry = {
        ...cached,
        sessionId,
        results: data.results || data.output || [],
        stats: data.stats || cached?.stats || null,
        title: data.title || cached?.title || 'Correction',
        timestamp: data.timestamp || cached?.timestamp || new Date().toISOString(),
      };

      setCurrentCorrection(entry);
      return entry;
    } catch (error) {
      console.error('[Corrector] Load session error:', error);
      if (cached) setCurrentCorrection(cached);
      return cached || null;
    }
  };

  /**
   * One page of graded files across the user's corrections, newest first.
   * `since` (ISO string or null) limits it to the dashboard's period.
   * Returns { files, hasMore }; throws on a failed request.
   * Not cached in context — the dashboard holds the rows while it is open.
   */
  const loadRecentCorrectionFiles = useCallback(async ({ limit = 8, offset = 0, since = null } = {}) => {
    const response = await fetch(WEBHOOKS.CORRECTION_RECENT_FILES, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: currentUser?.id, limit, offset, since }),
    });
    if (!response.ok) throw new Error(`Failed to load recent files: ${response.status}`);

    // n8n responds [{ files, hasMore }].
    let data = await response.json();
    if (Array.isArray(data)) data = data[0];

    const files = (data?.files || [])
      .filter(r => r?.sessionId)
      .map(r => ({
        sessionId: r.sessionId,
        title: r.title || 'Correction',
        timestamp: r.timestamp || null,
        index: Number(r.index) || 0,
        filename: r.filename || 'Unknown',
        percentage: r.percentage ?? null,
        confidence: r.confidence || null,
        scan_failed: !!r.scan_failed,
        ocr_failed: !!r.ocr_failed,
        // Same shape as a saved result, so reviewReason/fileStatus work on rows.
        injection: { flagged: !!r.injection_flagged },
        reviewed: !!r.reviewed,
      }));

    return { files, hasMore: !!data?.hasMore };
  }, [currentUser?.id]);

  /**
   * Delete a correction on the server, then from local state. Returns whether it worked.
   */
  const deleteCorrectionFromHistory = async (sessionId) => {
    if (!(await deleteOnServer(WEBHOOKS.DELETE_CORRECTION, sessionId))) return false;
    setCorrectionHistory(prev => prev.filter(c => c.sessionId !== sessionId));
    if (currentCorrection?.sessionId === sessionId) setCurrentCorrection(null);
    return true;
  };

  const createNewCorrection = () => {
    setCurrentCorrection(null);
  };

  // ===== KNOWLEDGE BASE FUNCTIONS =====

  const loadKnowledgeBaseFiles = async (userId) => {
    try {
      const response = await fetch(WEBHOOKS.LOAD_KB_FILES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      if (!response.ok) throw new Error('Failed to load files');
      const data = await response.json();
      // Handle all shapes n8n may return
      let files = [];
      if (Array.isArray(data)) {
        // Already a flat array
        files = data;
      } else if (data && typeof data === 'object') {
        if (Array.isArray(data.data)) {
          // { data: [...] }
          files = data.data;
        } else if (data.id) {
          // Single object — wrap it
          files = [data];
        }
      }
      const normalized = files.map(f => ({
        id: f.id,
        name: f.document_title,
        size: parseInt(f.size, 10),
        type: f.file_type,
        uploadedAt: f.created_at,
        cloudFileId: null,
        cloudUrl: null
      }));
      setKnowledgeBaseFiles(normalized);
    } catch (error) {
      console.error('[KB] Load KB files error:', error);
    }
  };

  const addKnowledgeBaseFile = (file) => {
    setKnowledgeBaseFiles(prev => [...prev, file]);
  };

  const removeKnowledgeBaseFile = (fileId) => {
    setKnowledgeBaseFiles(prev => prev.filter(f => f.id !== fileId));
    setSelectedFiles(prev => prev.filter(id => id !== fileId));
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileId)) return prev.filter(id => id !== fileId);
      return [...prev, fileId];
    });
  };

  const selectBook = (bookValue, bookLabel = null) => {
    const isDeselecting = selectedBook === bookValue;
    setSelectedBook(isDeselecting ? null : bookValue);
    setSelectedBookLabel(isDeselecting ? null : bookLabel);
  };

  const clearSelectedBook = () => {
    setSelectedBook(null);
    setSelectedBookLabel(null);
  };

  // ===== DATA SOURCE FUNCTIONS =====
  //
  // The browser never holds database credentials. `createDataSource` is the
  // only call that carries them, and it is one-way: the server stores them
  // encrypted and returns a connection record with an id but no secrets.
  // Every later call — test, delete, and the chat payload — refers to the
  // connection by id alone.

  const loadDataSources = useCallback(async () => {
    setDataSourcesLoading(true);
    setDataSourcesError(null);
    try {
      const response = await fetch(WEBHOOKS.DATASOURCES_LIST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ u_id: currentUser?.id || 'guest', variant: APP_VARIANT })
      });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const data = await response.json();
      // n8n commonly wraps single results in an array; unwrap defensively.
      const list = Array.isArray(data) ? data : (data?.dataSources || []);
      setDataSources(Array.isArray(list) ? list : []);
      return list;
    } catch (error) {
      // Surfaced in the UI rather than swallowed — a datasource list that
      // silently comes back empty is indistinguishable from "none connected".
      console.error('Failed to load data sources:', error);
      setDataSourcesError(error.message || 'Failed to load data sources');
      return null;
    } finally {
      setDataSourcesLoading(false);
    }
  }, [currentUser?.id]);

  const createDataSource = useCallback(async (credentials) => {
    const response = await fetch(WEBHOOKS.DATASOURCE_CREATE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        u_id: currentUser?.id || 'guest',
        variant: APP_VARIANT,
        ...credentials
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.error) {
      throw new Error(data?.message || data?.error || `Server returned ${response.status}`);
    }
    await loadDataSources();
    return data;
  }, [currentUser?.id, loadDataSources]);

  const testDataSource = useCallback(async (dataSourceId) => {
    const response = await fetch(WEBHOOKS.DATASOURCE_TEST, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ u_id: currentUser?.id || 'guest', id: dataSourceId })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || `Server returned ${response.status}`);
    }
    return data;
  }, [currentUser?.id]);


  // ===== TASKS =====
  // Follow-ups raised from a chat answer or written by hand. A task may be kept
  // for yourself or assigned to a colleague, so the list is "created by me OR
  // assigned to me" — the server decides that, we only send who is asking.
  //
  // Identity is the same client-supplied user id every other endpoint here uses.
  // That is consistent with the rest of the app but it is NOT authentication:
  // anyone can claim any id. Fine for the current demo; revisit before real users.
  // n8n wraps a single result in an array, and a query that matched NOTHING
  // comes back as [{success:true}] rather than []. So callers unwrap and then
  // keep only rows that actually carry an id — that check is what separates
  // "no rows" from a real row, and it is why an update the server refused
  // correctly reads as a failure instead of a silent success.
  const unwrap = (data) => (Array.isArray(data) ? data : (data ? [data] : []));

  const loadTasks = useCallback(async () => {
    if (!currentUser?.id) return [];
    try {
      const res = await fetch(WEBHOOKS.TASKS_LIST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ u_id: currentUser.id }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const rows = unwrap(await res.json()).filter((r) => r && r.id);
      setTasks(rows);
      return rows;
    } catch (err) {
      console.error('Load tasks error:', err);
      showNotification('Could not load tasks', 'error');
      return [];
    }
  }, [currentUser]);   // eslint-disable-line react-hooks/exhaustive-deps

  const createTask = async (task) => {
    if (!currentUser?.id) return null;
    const res = await fetch(WEBHOOKS.TASK_CREATE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...task,
        u_id: currentUser.id,
        u_name: currentUser.username || null,
      }),
    });
    if (!res.ok) throw new Error(`Server returned ${res.status}`);
    const row = unwrap(await res.json())[0];
    if (row && row.id) setTasks((prev) => [row, ...prev]);
    return row || null;
  };

  const updateTask = async (patch) => {
    if (!currentUser?.id || !patch?.id) return null;
    const res = await fetch(WEBHOOKS.TASK_UPDATE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...patch, u_id: currentUser.id }),
    });
    if (!res.ok) throw new Error(`Server returned ${res.status}`);
    const row = unwrap(await res.json())[0];
    // An empty result means the server refused it (not mine to change), so the
    // local list must not be updated as if it had worked.
    if (row && row.id) setTasks((prev) => prev.map((x) => (x.id === row.id ? row : x)));
    return row || null;
  };

  const deleteTask = async (id) => {
    if (!currentUser?.id || !id) return false;
    const res = await fetch(WEBHOOKS.TASK_DELETE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, u_id: currentUser.id }),
    });
    if (!res.ok) throw new Error(`Server returned ${res.status}`);
    const row = unwrap(await res.json())[0];
    const ok = !!(row && row.id);
    if (ok) setTasks((prev) => prev.filter((x) => x.id !== id));
    return ok;
  };

  // ===== SCHOLARSHIPS =====
  // Read-only against the CEDS scholarship schema, plus two generative calls.
  //
  // The split that matters: `check` returns the ENGINE's decision -- gates,
  // score, trace -- and nothing generative touches it. `explain` takes that
  // same decision and renders it as prose. If the two ever disagree, the
  // explanation is wrong, not the decision, and the page shows the trace
  // beside it so that is visible rather than hidden.
  //
  // n8n wraps a single result in an array and returns [{success:true}] for a
  // query that matched nothing, so the same unwrap() the tasks calls use
  // applies here too.
  const scholarshipPost = useCallback(async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, u_id: currentUser?.id || null }),
    });
    if (!res.ok) throw new Error(`Server returned ${res.status}`);
    return res.json();
  }, [currentUser]);

  const loadScholarshipApplicants = useCallback(async (filters = {}) => {
    const data = await scholarshipPost(WEBHOOKS.SCHOLARSHIP_APPLICANTS, filters);
    return unwrap(data).filter((r) => r && r.applicant_ref);
  }, [scholarshipPost]);

  const checkScholarshipEligibility = useCallback(async (args) => {
    const row = unwrap(await scholarshipPost(WEBHOOKS.SCHOLARSHIP_CHECK, args))[0];
    return row || {};
  }, [scholarshipPost]);

  const explainScholarshipDecision = useCallback(async (args) => {
    const row = unwrap(await scholarshipPost(WEBHOOKS.SCHOLARSHIP_EXPLAIN, args))[0];
    return (row && (row.explanation || row.text)) || '';
  }, [scholarshipPost]);

  const loadScholarshipPipeline = useCallback(async (args) => {
    const row = unwrap(await scholarshipPost(WEBHOOKS.SCHOLARSHIP_PIPELINE, args))[0];
    return row || null;
  }, [scholarshipPost]);

  // Returns { letter } on success and { message } when the server refuses --
  // drafting an award letter for someone who was not awarded, for instance.
  // The refusal is deliberate, so its reason is passed through rather than
  // flattened into an empty string.
  const draftScholarshipLetter = useCallback(async (args) => {
    const row = unwrap(await scholarshipPost(WEBHOOKS.SCHOLARSHIP_LETTER, args))[0] || {};
    return { letter: row.letter || row.text || '', message: row.message || '' };
  }, [scholarshipPost]);

  const loadDirectory = useCallback(async () => {
    try {
      const res = await fetch(WEBHOOKS.USERS_DIRECTORY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const rows = unwrap(await res.json()).filter((r) => r && r.u_id);
      setDirectory(rows);
      return rows;
    } catch (err) {
      console.error('Load directory error:', err);
      return [];
    }
  }, []);

  const deleteDataSource = useCallback(async (dataSourceId) => {
    const response = await fetch(WEBHOOKS.DATASOURCE_DELETE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ u_id: currentUser?.id || 'guest', id: dataSourceId })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.message || `Server returned ${response.status}`);
    }
    // Deleting the connection the chat is pointed at must also clear the
    // selection, or the next message references a datasource that is gone.
    setSelectedDataSource((prev) => {
      if (prev !== dataSourceId) return prev;
      setSelectedDataSourceLabel(null);
      return null;
    });
    await loadDataSources();
  }, [currentUser?.id, loadDataSources]);

  // Toggling: selecting the already-selected source disconnects it, matching
  // selectBook's behaviour so the two KB selections feel the same.
  const selectDataSource = useCallback((dataSourceId, label = null) => {
    setSelectedDataSource((prev) => {
      const isDeselecting = prev === dataSourceId;
      setSelectedDataSourceLabel(isDeselecting ? null : label);
      return isDeselecting ? null : dataSourceId;
    });
  }, []);

  const clearSelectedDataSource = useCallback(() => {
    setSelectedDataSource(null);
    setSelectedDataSourceLabel(null);
  }, []);

  // ===== CHAT FUNCTIONS =====

  const toggleTheme = () => {
    setTheme(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      // Toggling back to what the OS shows drops the override, so the app
      // goes back to following the system.
      if (next === systemTheme()) localStorage.removeItem(STORAGE_KEYS.THEME);
      else localStorage.setItem(STORAGE_KEYS.THEME, next);
      return next;
    });
  };

  const createNewChat = (title = 'New Chat') => {
    if (currentChat && currentChat.sessionId && (!currentChat.messages || currentChat.messages.length === 0)) {
      return currentChat;
    }

    const sessionId = crypto.randomUUID();
    const newChat = {
      sessionId,
      title,
      messages: [],
      messagesLoaded: true,
      timestamp: new Date().toISOString()
    };

    setChatHistory(prev => {
      const exists = prev.find(c => c.sessionId === sessionId);
      if (exists) return prev;
      return [newChat, ...prev];
    });

    setCurrentChat(newChat);
    return newChat;
  };

  const loadChat = async (sessionId) => {
    const chat = chatHistory.find(c => c.sessionId === sessionId || c.id === sessionId);

    if (!chat) {
      console.error('❌ Chat not found in history:', sessionId);
      return;
    }

    const normalizedChat = normalizeChat(chat);
    if (!normalizedChat) return;

    if (!normalizedChat.messagesLoaded || !normalizedChat.messages || normalizedChat.messages.length === 0) {
      const messages = await loadChatMessages(normalizedChat.sessionId, currentUser.id);
      const updatedChat = { ...normalizedChat, messages, messagesLoaded: true };

      setChatHistory(prev =>
        prev.map(c => {
          const cSessionId = c.sessionId || c.id;
          return cSessionId === normalizedChat.sessionId ? updatedChat : c;
        })
      );
      setCurrentChat(updatedChat);
    } else {
      setCurrentChat(normalizedChat);
    }
  };

  // Delete a chat on the server, then from history. Returns whether it worked.
  const deleteChat = async (sessionId) => {
    if (!(await deleteOnServer(WEBHOOKS.DELETE_CHAT, sessionId))) return false;
    setChatHistory(prev => prev.filter(c => (c.sessionId || c.id) !== sessionId));
    if (currentChat?.sessionId === sessionId) setCurrentChat(null);
    return true;
  };

  const addMessage = (message, targetChat = null) => {
    const chatToUpdate = targetChat || currentChat;
    if (!chatToUpdate) return;

    const sessionId = chatToUpdate.sessionId || chatToUpdate.id;
    if (!sessionId) return;

    setCurrentChat(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        sessionId: prev.sessionId || prev.id,
        messages: [...(prev?.messages || []), message],
        timestamp: new Date().toISOString()
      };
    });

    setChatHistory(prev =>
      prev.map(chat => {
        const chatSessionId = chat.sessionId || chat.id;
        if (chatSessionId === sessionId) {
          return {
            ...chat,
            sessionId: chatSessionId,
            messages: [...(chat.messages || []), message],
            timestamp: new Date().toISOString()
          };
        }
        return chat;
      })
    );
  };

  // Remove messages from a chat in this browser only (Chat uses it to clear a
  // failed reply before it is retried). Nothing is deleted on the server.
  const removeMessages = (messageIds, targetChat = null) => {
    const chatToUpdate = targetChat || currentChat;
    const sessionId = chatToUpdate?.sessionId || chatToUpdate?.id;
    if (!sessionId || !Array.isArray(messageIds) || messageIds.length === 0) return;
    const keep = (messages) => (messages || []).filter(m => !messageIds.includes(m?.id));

    setCurrentChat(prev => {
      if (!prev || (prev.sessionId || prev.id) !== sessionId) return prev;
      return { ...prev, messages: keep(prev.messages) };
    });

    setChatHistory(prev =>
      prev.map(chat =>
        (chat.sessionId || chat.id) === sessionId ? { ...chat, messages: keep(chat.messages) } : chat
      )
    );
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const value = {
    // Theme
    theme,
    toggleTheme,

    // User & Auth
    currentUser,
    isAuthenticated,
    login,
    signup,
    logout,
    setGuestUser,

    // Chat
    chatHistory,
    currentChat,
    createNewChat,
    loadChat,
    deleteChat,
    addMessage,
    removeMessages,
    generateChatHeader,
    updateChatTitle,

    // Document History
    documentHistory,
    currentDocument,
    correctionHistory,
    currentCorrection,
    loadCorrectionHistory,
    upsertCorrectionHistory,
    deleteCorrectionFromHistory,
    loadCorrectionSession,
    loadRecentCorrectionFiles,
    createNewCorrection,
    loadDocumentHistory,
    loadDocument,
    createNewDocument,
    deleteDocument,
    upsertDocumentInHistory,
    trackDocumentSave,
    keepDocumentCopy,

    // Knowledge Base
    knowledgeBaseFiles,
    selectedFiles,
    loadKnowledgeBaseFiles,
    addKnowledgeBaseFile,
    removeKnowledgeBaseFile,
    toggleFileSelection,
    selectedBook,
    selectedBookLabel,
    selectBook,
    clearSelectedBook,

    // Data Sources (external databases)
    dataSources,
    dataSourcesLoading,
    dataSourcesError,
    loadDataSources,
    createDataSource,
    testDataSource,
    deleteDataSource,
    tasks,
    directory,
    loadTasks,
    createTask,
    updateTask,
    deleteTask,
    loadScholarshipApplicants,
    checkScholarshipEligibility,
    explainScholarshipDecision,
    loadScholarshipPipeline,
    draftScholarshipLetter,
    loadDirectory,
    selectedDataSource,
    selectedDataSourceLabel,
    selectDataSource,
    clearSelectedDataSource,

    // Notifications
    notification,
    showNotification,

    // Webhooks
    WEBHOOKS
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};